/**
 * Centralized API Client
 * 
 * Provides a unified interface for all API calls with:
 * - Automatic error handling
 * - Type-safe responses
 * - Request/response interceptors
 * - Retry logic for failed requests
 * - Automatic JSON parsing
 * 
 * @example
 * // GET request
 * const { data, error } = await api.get<UserData>('/api/users');
 * 
 * // POST request
 * const { data, error } = await api.post('/api/users', { name: 'John' });
 * 
 * // With options
 * const { data } = await api.get('/api/data', { 
 *   params: { page: 1 },
 *   retry: 3 
 * });
 */

// =============================================================================
// Types
// =============================================================================

export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

export interface ApiError {
  status: number;
  message: string;
  code?: string;
  responseData?: Record<string, unknown>;
}

export interface ApiResult<T> {
  data: T | null;
  error: ApiError | null;
  isLoading?: boolean;
}

interface RequestOptions {
  params?: Record<string, string | number | boolean | undefined>;
  headers?: Record<string, string>;
  retry?: number;
  retryDelay?: number;
  timeout?: number;
  signal?: AbortSignal;
}

// =============================================================================
// Error Messages (Hebrew)
// =============================================================================

const ERROR_MESSAGES: Record<number, string> = {
  400: 'הבקשה אינה תקינה',
  401: 'יש להתחבר מחדש למערכת',
  403: 'אין לך הרשאה לבצע פעולה זו', // Also covers CSRF failures
  404: 'המשאב המבוקש לא נמצא',
  408: 'הבקשה נכשלה עקב תפוגת זמן',
  409: 'קיימת התנגשות עם נתונים קיימים',
  422: 'הנתונים שהוזנו אינם תקינים',
  429: 'יותר מדי בקשות, נסה שוב מאוחר יותר',
  500: 'שגיאת שרת פנימית',
  502: 'השרת אינו זמין כרגע',
  503: 'השירות אינו זמין זמנית',
  504: 'השרת לא הגיב בזמן',
};

const DEFAULT_ERROR_MESSAGE = 'אירעה שגיאה לא צפויה';
const NETWORK_ERROR_MESSAGE = 'שגיאת תקשורת - בדוק את החיבור לאינטרנט';
const TIMEOUT_ERROR_MESSAGE = 'הבקשה נכשלה עקב תפוגת זמן';

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Build URL with query parameters
 */
function buildUrl(endpoint: string, params?: RequestOptions['params']): string {
  if (!params) return endpoint;

  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value));
    }
  });

  const queryString = searchParams.toString();
  return queryString ? `${endpoint}?${queryString}` : endpoint;
}

/**
 * Get user-friendly error message
 */
function getErrorMessage(status: number, serverMessage?: string): string {
  // Prefer server message if it's in Hebrew (likely user-friendly)
  if (serverMessage && /[\u0590-\u05FF]/.test(serverMessage)) {
    return serverMessage;
  }
  return ERROR_MESSAGES[status] || serverMessage || DEFAULT_ERROR_MESSAGE;
}

/**
 * Sleep utility for retry delay
 */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// =============================================================================
// API Client Class
// =============================================================================

class ApiClient {
  private baseUrl: string;
  private defaultHeaders: Record<string, string>;
  private defaultTimeout: number;
  private defaultRetry: number;
  private defaultRetryDelay: number;

  constructor() {
    this.baseUrl = '';
    this.defaultHeaders = {
      'Content-Type': 'application/json',
      // CSRF protection header - required for state-changing requests
      'X-Requested-With': 'XMLHttpRequest',
    };
    this.defaultTimeout = 30000; // 30 seconds
    this.defaultRetry = 0;
    this.defaultRetryDelay = 1000;
  }

  /**
   * Make a fetch request with error handling and retries
   */
  private async request<T>(
    method: string,
    endpoint: string,
    body?: unknown,
    options: RequestOptions = {}
  ): Promise<ApiResult<T>> {
    const {
      params,
      headers = {},
      retry = this.defaultRetry,
      retryDelay = this.defaultRetryDelay,
      timeout = this.defaultTimeout,
      signal,
    } = options;

    const url = buildUrl(`${this.baseUrl}${endpoint}`, params);
    
    // Create abort controller for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    
    // Combine signals
    const combinedSignal = signal 
      ? AbortSignal.any([signal, controller.signal])
      : controller.signal;

    let lastError: ApiError | null = null;
    let attempts = 0;

    while (attempts <= retry) {
      try {
        const response = await fetch(url, {
          method,
          headers: {
            ...this.defaultHeaders,
            ...headers,
          },
          body: body ? JSON.stringify(body) : undefined,
          credentials: 'include',
          signal: combinedSignal,
        });

        clearTimeout(timeoutId);

        // Parse response
        let responseData: ApiResponse<T>;
        const contentType = response.headers.get('content-type');
        
        if (contentType?.includes('application/json')) {
          try {
            responseData = await response.json();
          } catch {
            // JSON parsing failed - might be HTML disguised as JSON (proxy/filter intercept)
            return {
              data: null,
              error: {
                status: response.status,
                message: 'התקבלה תשובה לא תקינה מהשרת',
              },
            };
          }
        } else {
          // Handle non-JSON responses
          const text = await response.text();
          
          // Check if response is HTML (likely a proxy/filter block page)
          const trimmedText = text.trim().toLowerCase();
          if (trimmedText.startsWith('<!doctype') || trimmedText.startsWith('<html')) {
            return {
              data: null,
              error: {
                status: response.status,
                message: 'התקבלה תשובה לא צפויה מהשרת. ייתכן שהגישה לאתר חסומה על ידי מסנן התוכן.',
              },
            };
          }
          
          responseData = { success: response.ok, message: text };
        }

        // Handle successful response
        if (response.ok && responseData.success !== false) {
          return {
            data: responseData.data ?? (responseData as unknown as T),
            error: null,
          };
        }

        // Handle error response
        const errorMessage = getErrorMessage(
          response.status,
          responseData.message || responseData.error
        );

        lastError = {
          status: response.status,
          message: errorMessage,
          responseData: responseData as unknown as Record<string, unknown>,
        };

        // Don't retry client errors (4xx) except for 408, 429
        if (response.status >= 400 && response.status < 500 && 
            response.status !== 408 && response.status !== 429) {
          break;
        }

      } catch (err) {
        clearTimeout(timeoutId);

        if (err instanceof Error) {
          if (err.name === 'AbortError') {
            lastError = {
              status: 408,
              message: TIMEOUT_ERROR_MESSAGE,
            };
          } else {
            lastError = {
              status: 0,
              message: NETWORK_ERROR_MESSAGE,
            };
          }
        }
      }

      attempts++;
      if (attempts <= retry) {
        await sleep(retryDelay * attempts); // Exponential backoff
      }
    }

    return {
      data: null,
      error: lastError || { status: 0, message: DEFAULT_ERROR_MESSAGE },
    };
  }

  // ===========================================================================
  // Public Methods
  // ===========================================================================

  /**
   * GET request
   */
  async get<T>(endpoint: string, options?: RequestOptions): Promise<ApiResult<T>> {
    return this.request<T>('GET', endpoint, undefined, options);
  }

  /**
   * POST request
   */
  async post<T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<ApiResult<T>> {
    return this.request<T>('POST', endpoint, body, options);
  }

  /**
   * PUT request
   */
  async put<T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<ApiResult<T>> {
    return this.request<T>('PUT', endpoint, body, options);
  }

  /**
   * PATCH request
   */
  async patch<T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<ApiResult<T>> {
    return this.request<T>('PATCH', endpoint, body, options);
  }

  /**
   * DELETE request
   */
  async delete<T>(endpoint: string, options?: RequestOptions): Promise<ApiResult<T>> {
    return this.request<T>('DELETE', endpoint, undefined, options);
  }

  /**
   * Download file (returns Blob)
   */
  async downloadFile(endpoint: string, body?: unknown): Promise<ApiResult<Blob>> {
    try {
      const response = await fetch(endpoint, {
        method: body ? 'POST' : 'GET',
        headers: body ? { 
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest', // CSRF protection
        } : {
          'X-Requested-With': 'XMLHttpRequest', // CSRF protection
        },
        body: body ? JSON.stringify(body) : undefined,
        credentials: 'include',
      });

      if (!response.ok) {
        return {
          data: null,
          error: {
            status: response.status,
            message: getErrorMessage(response.status),
          },
        };
      }

      // Check if response is HTML (likely a proxy/filter block page)
      const responseContentType = response.headers.get('content-type');
      if (responseContentType?.includes('text/html')) {
        const text = await response.text();
        const trimmedText = text.trim().toLowerCase();
        if (trimmedText.startsWith('<!doctype') || trimmedText.startsWith('<html')) {
          return {
            data: null,
            error: {
              status: response.status,
              message: 'התקבלה תשובה לא צפויה מהשרת. ייתכן שהגישה לאתר חסומה על ידי מסנן התוכן.',
            },
          };
        }
      }

      const blob = await response.blob();
      return { data: blob, error: null };
    } catch {
      return {
        data: null,
        error: { status: 0, message: NETWORK_ERROR_MESSAGE },
      };
    }
  }
}

// =============================================================================
// Export Singleton Instance
// =============================================================================

export const api = new ApiClient();

// =============================================================================
// React Query Compatible Fetcher
// =============================================================================

/**
 * Simple fetcher function for React Query
 * Throws error on failure (as React Query expects)
 * 
 * @example
 * const { data } = useQuery({
 *   queryKey: ['users'],
 *   queryFn: () => fetcher('/api/users')
 * });
 */
export async function fetcher<T>(url: string, options?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest', // CSRF protection
    },
    credentials: 'include',
    ...options,
  });

  if (!response.ok) {
    const errorMessage = getErrorMessage(response.status);
    throw new Error(errorMessage);
  }

  const contentType = response.headers.get('content-type');
  if (contentType?.includes('application/json')) {
    return response.json();
  }
  
  return response.text() as unknown as T;
}

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Download a blob as a file
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  a.remove();
}

/**
 * Check if error is an authentication error
 */
export function isAuthError(error: ApiError | null): boolean {
  return error?.status === 401;
}

/**
 * Check if error is a permission error
 */
export function isPermissionError(error: ApiError | null): boolean {
  return error?.status === 403;
}

/**
 * Check if error is a network error
 */
export function isNetworkError(error: ApiError | null): boolean {
  return error?.status === 0;
}
